import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { ArrowBack, ArrowForward } from '@mui/icons-material'
import React, { useState } from 'react'
import OtpInput from 'react-otp-input'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '../../../store'
import { VerificationActions, VerificationSelector } from '../../../store/Verification'
import { StorageUtils } from '../../../store/Verification/storage'
import { CustomTextField } from '../../CustomTextField/CustomTextField'
import { OutlinedButton } from '../../Button/OutlinedButton'
import { useSnackbar } from '../../../hooks/snackbar/useSnackbar'

interface AuthModalProps {
  open: boolean
  onSuccess: () => void
}

type Step = 'email' | 'otp'

export const AuthModal: React.FC<AuthModalProps> = ({ open, onSuccess }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const dispatch = useDispatch<AppDispatch>()
  const { showSuccess, showError } = useSnackbar()

  const [step, setStep] = useState<Step>('email')
  const [emailError, setEmailError] = useState('')

  const {
    requestLoading,
    verifyLoading,
    resendLoading,
    inputEmail: email,
    otp,
  } = useSelector(VerificationSelector.getVerificationState)

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value
    setEmailError('')
    dispatch(VerificationActions.setInputEmail(newEmail))
  }

  const handleOtpChange = (value: string) => {
    dispatch(VerificationActions.setOtp(value))
  }

  const handleSendOtp = async () => {
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address')
      return
    }
    const resultAction: any = await dispatch(VerificationActions.sendOtpAsync(email))
    if (VerificationActions.sendOtpAsync.fulfilled.match(resultAction)) {
      if (resultAction.payload.success) {
        showSuccess(resultAction.payload.message)
        setStep('otp')
      }
    } else {
      showError(resultAction.payload?.message || 'Failed to send verification code')
    }
  }

  const handleVerifyOtp = async () => {
    try {
      const resultAction: any = await dispatch(
        VerificationActions.verifyOtpAsync({ email, otp })
      )
      if (VerificationActions.verifyOtpAsync.fulfilled.match(resultAction)) {
        StorageUtils.setVerificationState({
          email: resultAction.payload.user.email,
          isVerified: true,
          token: resultAction.payload.token,
          timestamp: Date.now(),
        })
        dispatch(VerificationActions.resetFormFields())
        onSuccess()
      }
      if (VerificationActions.verifyOtpAsync.rejected.match(resultAction)) {
        showError((resultAction.payload as any)?.message || 'Invalid verification code')
      }
    } catch (err) {
      showError('Something went wrong. Please try again.')
    }
  }

  const handleResendOtp = async () => {
    try {
      const resultAction: any = await dispatch(VerificationActions.sendOtpAsync(email))
      if (VerificationActions.sendOtpAsync.fulfilled.match(resultAction)) {
        showSuccess(resultAction.payload.message || 'Verification code resent')
      } else {
        showError(resultAction?.payload?.error || 'Failed to resend code')
      }
    } catch (err) {
      showError('Something went wrong. Please try again.')
    }
  }

  const handleBack = () => {
    dispatch(VerificationActions.setOtp(''))
    setStep('email')
  }

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 2,
        },
      }}
    >
      <DialogContent>
        {step === 'email' ? (
          <Stack alignItems="center" spacing={3}>
            <Stack alignItems="center" spacing={1}>
              <Typography variant="h5" fontWeight={600} textAlign="center">
                Sign in to view your profile
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Enter your email to receive a verification code
              </Typography>
            </Stack>

            <CustomTextField
              label="Email address"
              name="email"
              value={email}
              onChange={handleEmailChange}
              fullWidth
              placeholder="john.doe@gmail.com"
              required
              error={!!emailError}
              helperText={emailError}
              onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
            />

            <Button
              variant="contained"
              fullWidth
              size="large"
              sx={{ py: 1.5 }}
              endIcon={<ArrowForward />}
              onClick={handleSendOtp}
              disabled={!email || requestLoading}
            >
              {requestLoading ? 'Sending...' : 'Send verification code'}
            </Button>
          </Stack>
        ) : (
          <Stack alignItems="center" spacing={3}>
            <Stack alignItems="center" spacing={1}>
              <Typography variant="h5" fontWeight={600} textAlign="center">
                Check your email
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                We've sent a verification code to{' '}
                <Typography component="span" fontWeight={600}>
                  {email}
                </Typography>
              </Typography>
            </Stack>

            <Box>
              <OtpInput
                value={otp}
                onChange={handleOtpChange}
                numInputs={6}
                renderInput={(props) => (
                  <input
                    {...props}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    onChange={(e) => {
                      e.target.value = e.target.value.replace(/[^0-9]/g, '')
                      if (props.onChange) props.onChange(e)
                    }}
                  />
                )}
                inputStyle={{
                  margin: '4px',
                  width: isMobile ? '40px' : '50px',
                  height: isMobile ? '40px' : '50px',
                  borderRadius: '8px',
                  fontSize: '20px',
                  backgroundColor: 'white',
                  border: `1px solid ${theme.palette.divider}`,
                  textAlign: 'center',
                  outline: 'none',
                }}
                inputType="text"
                shouldAutoFocus
              />
            </Box>

            <Stack spacing={1.5} width="100%">
              <Button
                variant="contained"
                fullWidth
                size="large"
                sx={{ py: 1.5 }}
                endIcon={<ArrowForward />}
                disabled={verifyLoading || !otp || otp.length !== 6}
                onClick={handleVerifyOtp}
              >
                {verifyLoading ? 'Verifying...' : 'Verify code'}
              </Button>
              <OutlinedButton fullWidth disabled={resendLoading} onClick={handleResendOtp}>
                {resendLoading ? 'Resending...' : 'Resend code'}
              </OutlinedButton>
              <OutlinedButton
                fullWidth
                sx={{ color: theme.palette.grey[600] }}
                onClick={handleBack}
                startIcon={<ArrowBack fontSize="small" />}
              >
                Change email address
              </OutlinedButton>
            </Stack>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  )
}
